import { type RefObject, useEffect, useEffectEvent } from 'react';
import { dialogClasses } from '@mui/material/Dialog';

/** What a keyboard shortcut in a record editor asks for. */
export type RecordShortcut = 'previous' | 'next' | 'finding';

type ShortcutKey = Pick<
    KeyboardEvent,
    'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey' | 'repeat'
>;

/**
 * Strg+←/→ steps to the record before/after, Strg+↑ up to the Befund a
 * description belongs to. Shift and Alt stay out: Strg+Shift+← selects words.
 * A held key does not race through the records.
 */
export function recordShortcut(event: ShortcutKey): RecordShortcut | null {
    if (
        !(event.ctrlKey || event.metaKey) ||
        event.altKey ||
        event.shiftKey ||
        event.repeat
    ) {
        return null;
    }
    switch (event.key) {
        case 'ArrowLeft':
            return 'previous';
        case 'ArrowRight':
            return 'next';
        case 'ArrowUp':
            return 'finding';
        default:
            return null;
    }
}

/**
 * Inside a text field the same keys jump by word or paragraph, and that stays
 * theirs — the shortcuts work wherever the cursor is NOT in text.
 */
export function isTextEntry(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }
    return (
        target.isContentEditable ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLInputElement &&
            !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(
                target.type
            ))
    );
}

/**
 * Listens for {@link recordShortcut} in the dialog `anchorRef` sits in. On the
 * dialog's own DOM rather than the window, so a prompt opened on top of it
 * (its own portal) keeps the keys to itself.
 */
export function useRecordShortcuts(
    anchorRef: RefObject<HTMLElement | null>,
    handlers: Partial<Record<RecordShortcut, () => void>>
) {
    const onKeyDown = useEffectEvent((event: KeyboardEvent) => {
        if (event.defaultPrevented || isTextEntry(event.target)) {
            return;
        }
        const shortcut = recordShortcut(event);
        const handler = shortcut ? handlers[shortcut] : undefined;
        if (handler) {
            event.preventDefault();
            handler();
        }
    });
    useEffect(() => {
        const dialog = anchorRef.current?.closest<HTMLElement>(
            `.${dialogClasses.root}`
        );
        if (!dialog) {
            return;
        }
        dialog.addEventListener('keydown', onKeyDown);
        return () => dialog.removeEventListener('keydown', onKeyDown);
    }, [anchorRef]);
}
